// ─── Personal Baseline Deviation View ─────────────────────────────────────────
//
// Shows each metric as deviation (z-score) from a 14-day rolling personal
// baseline. Clinically, deviation from personal baseline predicts depressive
// episodes better than absolute values (JAMA Psychiatry 2026, AUC 0.80).
//
// Each metric row shows three things at once:
//   1. WHERE are you now vs baseline? → z-score value + color coding
//   2. WHAT is the recent trend?       → 7-day sparkline with area fill
//   3. WHICH DIRECTION is it heading?  → trend arrow from 3-day slope
//
// Always visible (no expand/collapse) — designed for quick scanning.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo } from 'react';
import { DailyMetrics } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DeviationMetric {
  key: string;
  label: string;
  zScore: number;       // standard deviations from 14-day baseline
  absValue: string;     // human-readable current value
  baseline: string;     // human-readable baseline average
  trend: number[];      // last 7 z-scores (oldest → newest)
  direction: 'higher_better' | 'lower_better';
}

type TrendDir = 'strong-up' | 'up' | 'stable' | 'down' | 'strong-down';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mean(arr: number[]): number {
  if (arr.length === 0) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function stddev(arr: number[]): number {
  if (arr.length < 2) return 0;
  const avg = mean(arr);
  return Math.sqrt(arr.reduce((s, v) => s + (v - avg) ** 2, 0) / (arr.length - 1));
}

/**
 * Returns an accent color for a z-score, taking into account whether higher
 * values are better for this metric.
 */
function zColor(z: number, higherBetter: boolean): string {
  const adj = higherBetter ? z : -z;
  if (adj >=  1.0) return '#22C55E'; // well above baseline — good
  if (adj >=  0.4) return '#86EFAC'; // slightly above
  if (adj >= -0.4) return '#94A3B8'; // at baseline
  if (adj >= -1.0) return '#FDE047'; // slightly below
  if (adj >= -1.5) return '#F59E0B'; // moderately below
  return '#EF4444';                   // significantly below — concern
}

/** Human-readable z-score label. */
function zLabel(z: number): string {
  const abs = Math.abs(z);
  if (abs < 0.25) return 'At baseline';
  return `${z > 0 ? '+' : ''}${z.toFixed(1)} SD`;
}

/**
 * Compute a trend direction from the last 3 points of the z-score array.
 * Slope threshold: ±0.25 SD/day for weak, ±0.5 for strong.
 */
function trendDir(trend: number[]): TrendDir {
  if (trend.length < 3) return 'stable';
  const last3 = trend.slice(-3);
  const slope = (last3[last3.length - 1] - last3[0]) / (last3.length - 1);
  if (slope >  0.5) return 'strong-up';
  if (slope >  0.2) return 'up';
  if (slope < -0.5) return 'strong-down';
  if (slope < -0.2) return 'down';
  return 'stable';
}

/** Arrow color factoring in both trend direction AND whether up is good. */
function arrowColor(dir: TrendDir, higherBetter: boolean): string {
  if (dir === 'stable') return '#94A3B8';
  const isUp = dir === 'strong-up' || dir === 'up';
  const isGood = isUp === higherBetter;
  return isGood ? '#22C55E' : '#F59E0B';
}

// ─── Trend Arrow SVG ──────────────────────────────────────────────────────────

function TrendArrow({ dir, color }: { dir: TrendDir; color: string }) {
  const size = 14;
  const mid  = size / 2;

  // Arrow path directions
  if (dir === 'stable') {
    return (
      <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
        <path d="M2 7h10M9 4.5L11.5 7 9 9.5" stroke={color} strokeWidth="1.8"
          strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  const isUp = dir === 'strong-up' || dir === 'up';
  const strong = dir === 'strong-up' || dir === 'strong-down';

  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      {/* Shaft */}
      <line
        x1={mid} y1={isUp ? 11 : 3}
        x2={mid} y2={isUp ? 4  : 11}
        stroke={color} strokeWidth={strong ? 2 : 1.6} strokeLinecap="round"
      />
      {/* Arrowhead */}
      <path
        d={isUp
          ? `M${mid - 3} 6 L${mid} 3 L${mid + 3} 6`
          : `M${mid - 3} 8 L${mid} 11 L${mid + 3} 8`}
        stroke={color} strokeWidth={strong ? 2 : 1.6}
        strokeLinecap="round" strokeLinejoin="round" fill="none"
      />
      {/* Second arrowhead for strong */}
      {strong && (
        <path
          d={isUp
            ? `M${mid - 3} 9 L${mid} 6 L${mid + 3} 9`
            : `M${mid - 3} 5 L${mid} 8 L${mid + 3} 5`}
          stroke={color} strokeWidth={1.6}
          strokeLinecap="round" strokeLinejoin="round" fill="none"
        />
      )}
    </svg>
  );
}

// ─── Enhanced Sparkline ───────────────────────────────────────────────────────

function Sparkline({ data, higherBetter }: { data: number[]; higherBetter: boolean }) {
  if (data.length < 2) return null;

  const W = 88, H = 32, pad = 3;
  const minY = Math.min(...data, -2.5);
  const maxY = Math.max(...data, 2.5);
  const range = maxY - minY || 1;

  const xOf = (i: number) => pad + (i / (data.length - 1)) * (W - pad * 2);
  const yOf = (v: number) => H - pad - ((v - minY) / range) * (H - pad * 2);

  const zeroY  = yOf(0);
  const latestZ = data[data.length - 1];
  const color   = zColor(latestZ, higherBetter);

  const pointStr = data.map((v, i) => `${xOf(i)},${yOf(v)}`).join(' ');

  // Polygon for area fill: start at zero line, trace data, return to zero
  const fillPoly = [
    `${xOf(0)},${zeroY}`,
    ...data.map((v, i) => `${xOf(i)},${yOf(v)}`),
    `${xOf(data.length - 1)},${zeroY}`,
  ].join(' ');

  return (
    <svg width={W} height={H} className="flex-shrink-0">
      {/* Zero baseline */}
      <line
        x1={pad} y1={zeroY} x2={W - pad} y2={zeroY}
        stroke="#CBD5E1" strokeWidth={0.6} strokeDasharray="3 2"
      />
      {/* Area fill */}
      <polygon points={fillPoly} fill={color} fillOpacity={0.13} />
      {/* Trend line */}
      <polyline
        points={pointStr}
        fill="none" stroke={color} strokeWidth={1.6}
        strokeLinecap="round" strokeLinejoin="round"
      />
      {/* Latest dot */}
      <circle
        cx={xOf(data.length - 1)} cy={yOf(latestZ)}
        r={2.5} fill={color}
      />
    </svg>
  );
}

// ─── Horizontal Deviation Bar ─────────────────────────────────────────────────
//
// A thin horizontal track with a center line. The fill extends left (below
// baseline) or right (above baseline) proportional to the z-score magnitude.
// Makes the current position immediately obvious.

function DeviationBar({ zScore, higherBetter }: { zScore: number; higherBetter: boolean }) {
  const MAX_Z    = 2.5;
  const W        = 120;
  const H        = 6;
  const center   = W / 2;
  const clamped  = Math.max(-MAX_Z, Math.min(MAX_Z, zScore));
  const fill     = Math.abs(clamped) / MAX_Z * (W / 2 - 1);
  const color    = zColor(zScore, higherBetter);
  const isPos    = clamped >= 0;

  return (
    <svg width={W} height={H} className="flex-shrink-0">
      {/* Track */}
      <rect x={0} y={H / 2 - 1} width={W} height={2} rx={1} fill="#E5E7EB" />
      {/* Fill */}
      {fill > 0 && (
        <rect
          x={isPos ? center : center - fill}
          y={H / 2 - 2.5}
          width={fill}
          height={5}
          rx={2}
          fill={color}
          fillOpacity={0.85}
        />
      )}
      {/* Center notch */}
      <rect x={center - 0.5} y={0} width={1} height={H} rx={0.5} fill="#CBD5E1" />
    </svg>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BaselineDeviation({ metrics }: { metrics: DailyMetrics[] }) {

  const deviations = useMemo<DeviationMetric[]>(() => {
    if (metrics.length < 7) return [];

    const sorted = [...metrics].sort((a, b) => a.date.localeCompare(b.date));

    const metricDefs: {
      key: keyof DailyMetrics;
      label: string;
      direction: 'higher_better' | 'lower_better';
      format: (v: number) => string;
      filter: (v: unknown) => v is number;
    }[] = [
      {
        key: 'sleep_score', label: 'Sleep Score', direction: 'higher_better',
        format: v => `${Math.round(v)}/100`,
        filter: (v): v is number => typeof v === 'number',
      },
      {
        key: 'hrv', label: 'HRV', direction: 'higher_better',
        format: v => `${Math.round(v)} ms`,
        filter: (v): v is number => typeof v === 'number' && v > 0,
      },
      {
        key: 'readiness', label: 'Readiness', direction: 'higher_better',
        format: v => `${Math.round(v)}/100`,
        filter: (v): v is number => typeof v === 'number',
      },
      {
        key: 'steps', label: 'Steps', direction: 'higher_better',
        format: v => `${Math.round(v).toLocaleString()}`,
        filter: (v): v is number => typeof v === 'number',
      },
      {
        key: 'total_sleep_mins', label: 'Sleep Duration', direction: 'higher_better',
        format: v => `${Math.floor(v / 60)}h ${Math.round(v % 60)}m`,
        filter: (v): v is number => typeof v === 'number',
      },
      {
        key: 'stress_score', label: 'Stress Resilience', direction: 'higher_better',
        format: v => `${Math.round(v)}/100`,
        filter: (v): v is number => typeof v === 'number',
      },
    ];

    const results: DeviationMetric[] = [];

    for (const def of metricDefs) {
      const allValues = sorted.map(m => m[def.key]).filter(def.filter);
      if (allValues.length < 7) continue;

      // Baseline: 14-day window excluding the latest day to avoid self-reference
      const baselineWindow = allValues.slice(-14, -1);
      if (baselineWindow.length < 5) continue;

      const bMean = mean(baselineWindow);
      const bSD   = stddev(baselineWindow);
      if (bSD === 0) continue;

      const latest = allValues[allValues.length - 1];
      const z      = (latest - bMean) / bSD;

      // 7-day z-score trend (for sparkline + direction)
      const recent7 = allValues.slice(-7);
      const trend   = recent7.map(v => (v - bMean) / bSD);

      results.push({
        key: def.key,
        label: def.label,
        zScore: Math.round(z * 10) / 10,
        absValue: def.format(latest),
        baseline: def.format(bMean),
        trend,
        direction: def.direction,
      });
    }

    return results;
  }, [metrics]);

  if (deviations.length === 0) return null;

  const aboveCount    = deviations.filter(d => d.zScore > 0.4).length;
  const belowCount    = deviations.filter(d => d.zScore < -0.4).length;

  return (
    <div className="card">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-bold text-arete-navy">Baseline Deviation</p>
          <p className="text-xs text-arete-muted mt-0.5">vs. your 14-day personal average</p>
        </div>
        <div className="flex items-center gap-2">
          {aboveCount > 0 && (
            <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">
              {aboveCount} above ↑
            </span>
          )}
          {belowCount > 0 && (
            <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
              {belowCount} below ↓
            </span>
          )}
        </div>
      </div>

      {/* ── Column headers (desktop) ── */}
      <div className="hidden sm:flex items-center gap-3 mb-1 px-1">
        <div className="flex-1 min-w-0" />
        <p className="text-[10px] font-semibold text-arete-muted uppercase tracking-wide w-[88px] text-center">
          7-Day Trend
        </p>
        <p className="text-[10px] font-semibold text-arete-muted uppercase tracking-wide w-[120px] text-center">
          Position
        </p>
        <p className="text-[10px] font-semibold text-arete-muted uppercase tracking-wide w-[56px] text-right">
          Deviation
        </p>
        <p className="text-[10px] font-semibold text-arete-muted uppercase tracking-wide w-[14px]" />
      </div>

      {/* ── Metric rows ── */}
      <div className="divide-y divide-gray-50">
        {deviations.map(d => {
          const color    = zColor(d.zScore, d.direction === 'higher_better');
          const dir      = trendDir(d.trend);
          const aColor   = arrowColor(dir, d.direction === 'higher_better');
          const zText    = zLabel(d.zScore);
          const isGood   = (d.direction === 'higher_better' ? d.zScore : -d.zScore) >= 0.4;
          const isBad    = (d.direction === 'higher_better' ? d.zScore : -d.zScore) <= -0.4;

          return (
            <div key={d.key} className="flex items-center gap-3 py-2.5 px-1">
              {/* Status dot + label + current value */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                  <p className="text-sm font-semibold text-arete-navy truncate">{d.label}</p>
                </div>
                <p className="text-xs text-arete-muted mt-0.5 ml-3.5">
                  <span className="font-medium" style={{ color: isBad ? color : isGood ? color : undefined }}>
                    {d.absValue}
                  </span>
                  <span className="text-[10px] ml-1">avg {d.baseline}</span>
                </p>
              </div>

              {/* 7-day sparkline */}
              <div className="hidden sm:block">
                <Sparkline data={d.trend} higherBetter={d.direction === 'higher_better'} />
              </div>

              {/* Deviation bar */}
              <div className="hidden sm:block">
                <DeviationBar zScore={d.zScore} higherBetter={d.direction === 'higher_better'} />
              </div>

              {/* Z-score text */}
              <div className="text-right flex-shrink-0 w-14">
                <p className="text-sm font-bold tabular-nums" style={{ color }}>
                  {d.zScore > 0 ? '+' : ''}{d.zScore.toFixed(1)}
                  <span className="text-[9px] font-normal ml-0.5">SD</span>
                </p>
                <p className="text-[9px] text-arete-muted leading-tight">{zText.split(' ').slice(1).join(' ')}</p>
              </div>

              {/* Trend arrow */}
              <div className="flex-shrink-0 flex items-center justify-center w-4">
                <TrendArrow dir={dir} color={aColor} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Legend ── */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 pt-3 border-t border-gray-50">
        {[
          { color: '#22C55E', label: '+1 SD  above' },
          { color: '#94A3B8', label: '±0.4 SD  baseline' },
          { color: '#F59E0B', label: '−1 SD  below' },
          { color: '#EF4444', label: '−1.5 SD  concern' },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
            <span className="text-[10px] text-arete-muted">{label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1 ml-auto">
          <TrendArrow dir="up" color="#22C55E" />
          <span className="text-[10px] text-arete-muted ml-1">improving</span>
          <TrendArrow dir="down" color="#F59E0B" />
          <span className="text-[10px] text-arete-muted ml-1">declining</span>
          <TrendArrow dir="stable" color="#94A3B8" />
          <span className="text-[10px] text-arete-muted ml-1">stable</span>
        </div>
      </div>
    </div>
  );
}
